import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { ReportFrame, reportMeta } from "./Frame";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { plural, shortDate } from "../kit";
import { formatCentsCompact } from "@shared/money";

/**
 * Most-frequently-ordered items.
 *
 * The buying decision this screen exists for needs two numbers at once — how
 * often a line is asked for, and how many units that adds up to — so both get
 * their own column rather than one being chosen for the reader. The clinic
 * codes underneath answer the third question a bulk buy raises: is this one
 * clinic's habit, or all five?
 *
 * 390px notes, because this is the report most likely to break there: the item
 * names off the W68 sheet run to forty-plus characters ("Acclean One Step
 * Varnish 5% Sodium Fluor"), so the name cell wraps rather than truncating — a
 * truncated supply name is unidentifiable, and identifying the line is the
 * entire point. If the four columns still outgrow the phone, the registry's own
 * table container is what scrolls; the page body never does.
 */

export function TopItems() {
  const data = useQuery(api.reports.topItems.data, {});

  return (
    <ReportFrame
      {...reportMeta("top-items")}
      question="What do they burn through?"
      data={data}
      count={data?.rows.length}
      empty={{
        title: "Nothing has been requested yet",
        body: "This ranks items by how often the five clinics ask for them. It fills in from the first request submitted.",
      }}
      footnote={
        data && data.rows.length > 0 ? (
          <>
            {data.rows.length < data.distinctItems
              ? `Top ${data.rows.length} of ${data.distinctItems} items requested`
              : `All ${data.distinctItems} items requested`}
            , from {data.countedLines.toLocaleString("en-CA")} request lines
            {data.since ? ` since ${shortDate(data.since)}` : ""}. Counted from
            what the clinics asked for, not what was dispatched
            {data.excludedCancelledLines > 0
              ? ` — ${data.excludedCancelledLines} cancelled ${plural(
                  data.excludedCancelledLines,
                  "line",
                )} excluded`
              : ""}
            .
          </>
        ) : null
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8">#</TableHead>
            <TableHead>Item</TableHead>
            <TableHead className="text-right">Requests</TableHead>
            <TableHead className="text-right">Units</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data?.rows.map((row) => (
            <TableRow key={row.itemId}>
              <TableCell className="num align-top text-muted-foreground">
                {row.rank}
              </TableCell>

              <TableCell className="whitespace-normal">
                {/* wrap, never truncate — a half-shown supply name is
                    unidentifiable, and `break-words` keeps even an unbroken
                    40-character token inside 390px */}
                <div className="font-medium break-words">{row.name}</div>
                <div className="text-xs break-words text-muted-foreground">
                  {row.subcategory}
                  {row.packSize ? ` · ${row.packSize}` : ""}
                  {row.requestedListValueCents !== null
                    ? ` · ${formatCentsCompact(row.requestedListValueCents)} at list`
                    : " · price pending"}
                  {/* The gap between asked-for and actually-ordered. An item
                      requested five times and ordered twice is the bulk-buy
                      candidate, so the gap is shown, not smoothed over. */}
                  {row.timesRequested > row.timesOrdered
                    ? ` · ${row.timesRequested - row.timesOrdered} not yet ordered`
                    : ""}
                </div>

                {/* Which clinics ask for it — a stock `secondary` badge, and
                    deliberately not kit's `<Pill>`: a clinic code is a tag, not
                    a status, and it must not take one of the four status
                    tones. */}
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {row.clinicCodes.map((code) => (
                    <Badge key={code} variant="secondary">
                      {code}
                    </Badge>
                  ))}
                </div>
              </TableCell>

              <TableCell className="num text-right align-top font-medium">
                {row.timesRequested}
              </TableCell>
              <TableCell className="num text-right align-top text-muted-foreground">
                {row.totalQty.toLocaleString("en-CA")}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </ReportFrame>
  );
}
